import { Map, GeoJSONSource } from "mapbox-gl";
import { RouteState } from "../../../api/edits/editRoute.responses";
import { MapBoxGLLineData } from "../../../ts/interfaces/mapbox.interfaces";

// pre:   - route has the route with route line
//        - map has mapbox object on which to display
// post:  - map with displayed route line and vertex handles
export function DisplayRouteLine(map: Map, route: RouteState) {
  map.on("load", () => {
    map.addSource("route_line_source", {
      type: "geojson",
      data: {
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          coordinates: FillDataForRouteLine(route),
        },
      },
    });
    map.addLayer({
      id: "route_line",
      type: "line",
      source: "route_line_source",
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": "rgb(150,25,25)",
        "line-width": 5,
      },
    });

    // Vertex handle circles (shown in draw mode)
    map.addSource("route_line_vertices_source", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: [],
      },
    });
    map.addLayer({
      id: "route_line_vertices",
      type: "circle",
      source: "route_line_vertices_source",
      paint: {
        "circle-radius": 6,
        "circle-color": "#ffffff",
        "circle-stroke-width": 2,
        "circle-stroke-color": "rgb(150,25,25)",
      },
    });
  });
}

// pre:   route object with route line from which data is created
// post:  returns array data with coordinates of route line
//        (returns empty array when fewer than 2 points to avoid degenerate LineString)
export function FillDataForRouteLine(route: RouteState): number[][] {
  if (route.linePoints.length < 2) return [];
  return route.linePoints.map((point) => [point.longitude, point.latitude]);
}

// pre:   route object with route line
// post:  returns GeoJSON FeatureCollection of vertex point features
export function FillDataForVertexHandles(route: RouteState): any {
  return {
    type: "FeatureCollection",
    features: route.linePoints.map((pt) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [pt.longitude, pt.latitude],
      },
      properties: {},
    })),
  };
}

// pre:   - route has the route with route line
//        - map has mapbox object on which to display
// post:  - map layer route line has updated data
export function UpdateRouteLine(map: Map, route: RouteState): void {
  if (map != null) {
    let layLine: any = map.getSource("route_line_source") as GeoJSONSource;
    if (layLine !== undefined) {
      let coords: number[][] = FillDataForRouteLine(route);
      let data: MapBoxGLLineData = {
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          coordinates: coords,
        },
      };
      layLine.setData(data);
    }
  }
}

// pre:   - map has mapbox object on which to display
//        - route has current route state
//        - showHandles: true to show vertex handles, false to hide
// post:  - vertex handles layer is shown or hidden
export function UpdateRouteLineVertices(
  map: Map,
  route: RouteState,
  showHandles: boolean
): void {
  if (!map) return;
  const src = map.getSource("route_line_vertices_source") as GeoJSONSource;
  if (!src) return;
  if (showHandles) {
    src.setData(FillDataForVertexHandles(route));
  } else {
    src.setData({ type: "FeatureCollection", features: [] });
  }
}
